/**
 * Serviço de Autenticação
 * Responsável por gerenciar login, logout e tokens JWT
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8090';
const AUTH_PREFIX = '/api/v1';  // Prefixo para rotas de autenticação

/**
 * Helper para extrair mensagem de erro de validação do Pydantic
 */
function extractErrorMessage(error: any): string {
  // Erro de validação do Pydantic (array de objetos)
  if (Array.isArray(error.detail)) {
    const validationErrors = error.detail.map((err: any) => {
      // Identificar campo específico
      if (err.loc && err.loc.includes('email')) {
        return 'E-mail inválido. Verifique o formato (ex: usuario@dominio.com)';
      }
      if (err.loc && err.loc.includes('password')) {
        return 'Senha inválida';
      }
      if (err.loc && err.loc.includes('full_name')) {
        return 'Nome inválido';
      }
      // Mensagem genérica do erro
      return err.msg || 'Erro de validação';
    });
    return validationErrors[0];
  }
  
  // Erro simples (string)
  return error.detail || 'Erro na requisição';
}

export interface LoginRequest {
  email: string;  // Mudado de username para email
  password: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  suspect_alerts_enabled?: boolean;  // NOVO: Feature flag para Suspect Alerts
  person_tracking_enabled?: boolean;  // Mantido por compatibilidade (deprecated)
  heatmap_zones_enabled?: boolean;  // 🔥 Feature flag para Mapa de Calor por Zona
  vehicle_people_flow_enabled?: boolean;  // Feature flag para Fluxo de Veículos
  exclude_employees_enabled?: boolean;  // 🚫 Feature flag para Exclusão de Funcionários
  emotion_analytics_enabled?: boolean;  // 🎭 Feature flag para Análise de Emoções
  logo_url?: string;  // NOVO: URL da logo da organização no S3
}

export interface GroupInfo {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
}

export interface GroupOrganization {
  id: string;
  name: string;
  slug: string;
  organization_type?: string;
  logo_url?: string;
}

export interface UserResponse {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'manager' | 'coordinator' | 'operator' | 'viewer' | 'partner';
  phone?: string;
  is_active: boolean;
  is_superuser: boolean;
  organization_id?: string;  // ID da organização do usuário
  group_id?: string;  // ID do grupo (se pertencer a um grupo)
  created_at: string;
  updated_at?: string;
  last_login?: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: UserResponse;
  organization?: Organization;
  group?: GroupInfo;  // Info do grupo (se user pertencer a um)
  organizations?: GroupOrganization[];  // Organizações do grupo
}

export interface CreateUserRequest {
  email: string;
  full_name: string;
  password: string;
  phone?: string;
  role: 'admin' | 'manager' | 'coordinator' | 'operator' | 'viewer';
  organization_id?: string;  // NOVO: Para superuser especificar organização
  group_id?: string;  // NOVO: Para superuser atribuir usuário a um grupo
}

export interface UpdateUserRequest {
  email?: string;
  full_name?: string;
  phone?: string;
  role?: 'admin' | 'manager' | 'coordinator' | 'operator' | 'viewer';
  is_active?: boolean;
  organization_id?: string;
  group_id?: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface AdminChangePasswordRequest {
  new_password: string;
  confirm_password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  new_password: string;
  confirm_password: string;
}

class AuthService {
  private reauthentication: Promise<void> | null = null;
  private readonly TOKEN_KEY = 'awflux_token';
  private readonly USER_KEY = 'awflux_user';
  private readonly ORG_KEY = 'awflux_organization';
  private readonly GROUP_KEY = 'awflux_group';
  private readonly GROUP_ORGS_KEY = 'awflux_group_organizations';

  /**
   * Realiza login do usuário
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const data = await this.requestLogin(credentials);
    return this.saveLogin(data);
  }

  private async requestLogin(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        detail: 'Erro ao fazer login' 
      }));
      const failure = new Error(extractErrorMessage(error)) as Error & { status?: number };
      // Callers distinguish wrong password (401/400), rate limit (429) and
      // server/network failures without parsing prose.
      failure.status = response.status;
      throw failure;
    }

    return response.json();
  }

  private saveLogin(data: LoginResponse): LoginResponse {
    localStorage.removeItem(this.ORG_KEY);
    localStorage.removeItem(this.GROUP_KEY);
    localStorage.removeItem(this.GROUP_ORGS_KEY);
    localStorage.removeItem('awflux_active_organization_id');
    
    // Salvar token, usuário e organização no localStorage
    this.setToken(data.access_token);
    this.setUser(data.user);
    if (data.organization) {
      this.setOrganization(data.organization);
    }
    // Salvar dados de grupo se existirem
    if (data.group) {
      this.setGroup(data.group);
    }
    if (data.organizations && data.organizations.length > 0) {
      this.setGroupOrganizations(data.organizations);
    }

    return data;
  }

  /** Explicit password verification through the existing login protocol. The
   * server has no refresh-token endpoint: never manufacture one or replay a POST.
   * Single-flight avoids concurrent dialogs issuing duplicate logins. Preserve
   * active tenant/preferences; do not silently switch to another identity.
   */
  reauthenticate(password: string): Promise<void> {
    if (this.reauthentication) return this.reauthentication;
    const current = this.getUser();
    const sessionToken = this.getToken();
    if (!current?.email || !sessionToken) return Promise.reject(new Error('Sessão expirada. Faça login novamente.'));
    this.reauthentication = this.requestLogin({ email: current.email, password }).then(data => {
      if (data.user.id !== current.id || this.getUser()?.id !== current.id || this.getToken() !== sessionToken) {
        throw new Error('Identidade mudou durante a autenticação. Faça login novamente.');
      }
      this.setToken(data.access_token);
      this.setUser(data.user);
    }).finally(() => { this.reauthentication = null; });
    return this.reauthentication;
  }

  /**
   * Realiza logout do usuário
   */
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.ORG_KEY);
    localStorage.removeItem(this.GROUP_KEY);
    localStorage.removeItem(this.GROUP_ORGS_KEY);
  }

  /**
   * Obtém token do localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Salva token no localStorage
   */
  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Obtém usuário do localStorage
   */
  getUser(): UserResponse | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Salva usuário no localStorage
   */
  setUser(user: UserResponse): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Obtém organização do localStorage (NOVO)
   */
  getOrganization(): Organization | null {
    const orgStr = localStorage.getItem(this.ORG_KEY);
    if (!orgStr) return null;
    
    try {
      return JSON.parse(orgStr);
    } catch {
      return null;
    }
  }

  /**
   * Salva organização no localStorage
   */
  setOrganization(organization: Organization): void {
    localStorage.setItem(this.ORG_KEY, JSON.stringify(organization));
  }

  /**
   * Obtém grupo do localStorage
   */
  getGroup(): GroupInfo | null {
    const groupStr = localStorage.getItem(this.GROUP_KEY);
    if (!groupStr) return null;
    try {
      return JSON.parse(groupStr);
    } catch {
      return null;
    }
  }

  /**
   * Salva grupo no localStorage
   */
  setGroup(group: GroupInfo): void {
    localStorage.setItem(this.GROUP_KEY, JSON.stringify(group));
  }

  /**
   * Obtém organizações do grupo do localStorage
   */
  getGroupOrganizations(): GroupOrganization[] {
    const orgsStr = localStorage.getItem(this.GROUP_ORGS_KEY);
    if (!orgsStr) return [];
    try {
      return JSON.parse(orgsStr);
    } catch {
      return [];
    }
  }

  /**
   * Salva organizações do grupo no localStorage
   */
  setGroupOrganizations(orgs: GroupOrganization[]): void {
    localStorage.setItem(this.GROUP_ORGS_KEY, JSON.stringify(orgs));
  }

  /**
   * Verifica se usuário está autenticado
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Verifica se usuário é admin
   */
  isAdmin(): boolean {
    const user = this.getUser();
    return user?.role === 'admin' || user?.is_superuser === true;
  }

  /**
   * Verifica se usuário tem um papel específico (NOVO)
   */
  hasRole(role: string): boolean {
    const user = this.getUser();
    if (!user) return false;
    if (user.role === 'partner') return role === 'partner';
    if (role === 'partner') return false;
    if (user.is_superuser) return true;

    const rolesHierarchy: Record<string, number> = {
      viewer: 0,
      operator: 1,
      manager: 2,
      admin: 3,
    };

    const userLevel = rolesHierarchy[user.role] || 0;
    const requiredLevel = rolesHierarchy[role] || 999;

    return userLevel >= requiredLevel;
  }

  /**
   * Obtém dados do usuário atual da API
   */
  async getCurrentUser(): Promise<UserResponse> {
    const response = await this.authenticatedRequest('/auth/me');
    return response.json();
  }

  /**
   * Lista todos os usuários (apenas admin)
   */
  async listUsers(): Promise<{ users: UserResponse[]; total: number }> {
    const response = await this.authenticatedRequest('/auth/users');
    return response.json();
  }

  /**
   * Cria novo usuário (apenas admin)
   */
  async createUser(userData: CreateUserRequest): Promise<UserResponse> {
    const response = await this.authenticatedRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao criar usuário' }));
      throw new Error(extractErrorMessage(error));
    }

    return response.json();
  }

  /**
   * Atualiza usuário (apenas admin)
   */
  async updateUser(userId: string, userData: UpdateUserRequest): Promise<UserResponse> {
    const response = await this.authenticatedRequest(`/auth/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao atualizar usuário' }));
      throw new Error(extractErrorMessage(error));
    }

    return response.json();
  }

  /**
   * Deleta usuário (apenas admin)
   */
  async deleteUser(userId: string): Promise<void> {
    const response = await this.authenticatedRequest(`/auth/users/${userId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao deletar usuário' }));
      throw new Error(extractErrorMessage(error));
    }
  }

  /**
   * Altera senha do usuário atual
   */
  async changePassword(data: ChangePasswordRequest): Promise<{ message: string; user_id: string }> {
    const response = await this.authenticatedRequest('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao alterar senha' }));
      throw new Error(extractErrorMessage(error));
    }

    return response.json();
  }

  /**
   * Admin altera senha de outro usuário
   */
  async adminChangePassword(userId: string, data: AdminChangePasswordRequest): Promise<{ message: string; user_id: string }> {
    const response = await this.authenticatedRequest(`/auth/admin/change-password/${userId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erro ao alterar senha' }));
      throw new Error(extractErrorMessage(error));
    }

    return response.json();
  }

  /**
   * Faz requisição autenticada
   */
  private async authenticatedRequest(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const token = this.getToken();

    if (!token) {
      throw new Error('Token não encontrado. Faça login novamente.');
    }

    // Adiciona prefixo /api/v1 se o endpoint for de autenticação
    const fullEndpoint = endpoint.startsWith('/auth') 
      ? `${AUTH_PREFIX}${endpoint}` 
      : endpoint;

    const activeOrgId = localStorage.getItem('awflux_active_organization_id');
    const response = await fetch(`${API_BASE_URL}${fullEndpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...(activeOrgId ? { 'X-Organization-Id': activeOrgId } : {}),
        ...options.headers,
      },
    });

    // Se token inválido, fazer logout
    if (response.status === 401) {
      this.logout();
      window.location.href = '/login';
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    return response;
  }

  /**
   * Solicita recuperação de senha via email
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        detail: 'Erro ao solicitar recuperação de senha' 
      }));
      throw new Error(extractErrorMessage(error));
    }

    return await response.json();
  }

  /**
   * Redefine senha usando token recebido por email
   */
  async resetPassword(
    token: string, 
    new_password: string, 
    confirm_password: string
  ): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, new_password, confirm_password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        detail: 'Erro ao redefinir senha' 
      }));
      throw new Error(extractErrorMessage(error));
    }

    return await response.json();
  }
}

export const authService = new AuthService();
